import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import API_URL from './config';

const spinAnimation = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// Add pulse animation for skeleton loader
const pulseAnimation = `
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.6;
    }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = spinAnimation + pulseAnimation;
document.head.appendChild(styleSheet);

// Add responsive styles for location boxes
const responsiveStyles = document.createElement("style");
responsiveStyles.type = "text/css";
responsiveStyles.innerText = `
  @media (min-width: 768px) {
    .box-container {
      flex-direction: row !important;
    }
    .location-box {
      max-width: calc(33.33% - 14px) !important;
      min-width: 250px !important;
    }
  }
  
  .locations-section {
    padding: 0 16px !important;
    box-sizing: border-box !important;
    width: 100% !important;
    max-width: 600px !important;
    margin: 16px auto 0 !important;
  }
  
  .box-container {
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  .location-box {
    margin: 0 0 16px 0 !important;
    box-sizing: border-box !important;
    width: 100% !important;
  }
`;
document.head.appendChild(responsiveStyles);



const SkeletonLoader = () => {
  const skeletonStyle = {
    backgroundColor: '#f0f0f0',
    borderRadius: '8px',
    animation: 'pulse 1.5s ease-in-out infinite',
    marginBottom: '16px',
  };

  return (
    <div style={{ maxWidth: '600px', width: '100%', padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <p style={{ fontSize: '18px', color: '#007bff', marginBottom: '10px' }}>
          🔍 Finding your brand...
        </p>
        <p style={{ fontSize: '14px', color: '#666' }}>
          Searching for the closest locations
        </p>
      </div>
      
      {/* Brand Info Skeleton */}
      <div style={{ ...skeletonStyle, height: '120px', marginBottom: '20px' }}></div>
      
      {/* Location Cards Skeleton */}
      {[1, 2, 3].map(i => (
        <div key={i} style={{ ...skeletonStyle, height: '150px', marginBottom: '16px' }}></div>
      ))}
    </div>
  );
};

const formatDistance = (distance) => {
  const miles = parseFloat(distance) * 0.621371;
  if (miles < 0.1) {
    return 'Less than 0.1 miles away';
  } else if (miles < 1) {
    return `${miles.toFixed(1)} miles away`;
  } else {
    return `${Math.round(miles)} miles away`;
  }
};

const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  // Format as (XXX) XXX-XXXX
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return phone; // Return original if can't format
};

function HomePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brands, setBrands] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [aiResults, setAiResults] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [locationResults, setLocationResults] = useState([]);
  const [locationInfo, setLocationInfo] = useState({
    gps: null,
    ip: null,
    loading: true
  });

  const handleSearch = async (e) => {
    e.preventDefault();
    if (selectedBrand) {
      window.open(selectedBrand.website, '_blank');
      return;
    }

    try {
      setIsLoading(true);
      setLocationResults([]); // Clear previous results
      
      const response = await axios.post(`${API_URL}/api/ai-search`, { 
        query: searchTerm
      });
      
      console.log('AI Search Response:', response.data);
      
      if (response.data && response.data.result) {
        setAiResults(response.data.result);
        setHasResults(true);

        try {
          const searchLocation = locationInfo.gps && !locationInfo.gps.error ? {
            latitude: locationInfo.gps.latitude,
            longitude: locationInfo.gps.longitude,
            accuracy: locationInfo.gps.accuracy
          } : {
            latitude: 33.6374071,
            longitude: -117.9096828,
            accuracy: 150
          };
          
          console.log('Sending to places API:', {
            brandName: response.data.result.brandName,
            location: searchLocation
          });
          
          const placesResponse = await axios.post(`${API_URL}/api/places-search`, {
            brandName: response.data.result.brandName,
            location: searchLocation
          });

          console.log('Places API Response:', placesResponse.data);

          if (placesResponse.data && Array.isArray(placesResponse.data.locations)) {
            console.log('Setting location results:', placesResponse.data.locations);
            setLocationResults(placesResponse.data.locations);
          } else {
            console.log('No locations in response:', placesResponse.data);
          }
        } catch (placesError) {
          console.error('Places API error:', placesError.response?.data || placesError);
        }
      }
    } catch (error) {
      setAiResults({ error: 'Search failed' });
    } finally {
      setIsLoading(false);
    }
  };



  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedBrand(null);
    setFocusedIndex(-1);
    
    // Always hide suggestions while typing
    setBrands([]);
  };

  const handleKeyDown = (e) => {
    // Handle Enter key to trigger search
    if (e.key === 'Enter') {
      if (focusedIndex >= 0 && brands.length > 0) {
        e.preventDefault();
        handleBrandSelect(brands[focusedIndex]);
      } else {
        // Simply let the form submit and trigger handleSearch
        return;
      }
    }
    
    // Keep arrow key navigation for accessibility, but it won't do anything unless suggestions are shown
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < brands.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSearchTerm(brand.name);
    setFocusedIndex(-1);
  };

  useEffect(() => {
    if (focusedIndex >= 0 && suggestionsRef.current && suggestionsRef.current.children[focusedIndex]) {
      const focusedElement = suggestionsRef.current.children[focusedIndex];
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [focusedIndex]);

  useEffect(() => {
    const geolocationOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    setLocationInfo(prev => ({ ...prev, loading: true }));

    if ("geolocation" in navigator) {
      const positionSuccess = (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setLocationInfo(prev => ({
          ...prev,
          gps: {
            latitude,
            longitude,
            accuracy,
            source: 'GPS'
          },
          loading: false
        }));
      };

      const positionError = (error) => {
        setLocationInfo(prev => ({
          ...prev,
          gps: { error: error.message },
          loading: false
        }));
      };

      navigator.geolocation.getCurrentPosition(
        positionSuccess,
        positionError,
        geolocationOptions
      );
    }
  }, []); // Empty dependency array to ensure it only runs once on mount

  const openInMaps = (location) => {
    // For Google Maps, use place_id if available for the most accurate business listing
    const googleMapsUrl = location.place_id 
      ? `https://www.google.com/maps/place/?q=place_id:${location.place_id}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.name + ' ' + location.address)}`;

    // Check if it's an iOS device
    const isIOS = /iPad|iPhone|iPod/.test(navigator.platform) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      // For Apple Maps, use Apple Place ID if available for the exact place
      let appleMapsUrl;
      
      if (location.applePlaceId) {
        // Use Apple Place ID for the most accurate business listing
        appleMapsUrl = `maps://maps.apple.com/?placeId=${location.applePlaceId}`;
        console.log('Apple Maps URL (Place ID):', appleMapsUrl);
        console.log('Opening exact place in Apple Maps:', location.name);
      } else if (location.latitude && location.longitude) {
        // Fallback: Use coordinates with high zoom to show the specific location
        // The z=18 parameter zooms in close to show the specific business
        appleMapsUrl = `maps://maps.apple.com/?ll=${location.latitude},${location.longitude}&z=18&q=${encodeURIComponent(location.name)}`;
        
        console.log('Apple Maps URL (coordinates fallback):', appleMapsUrl);
        console.log('Opening specific business at coordinates:', location.latitude, location.longitude);
        console.log('Business:', location.name);
        console.log('Address:', location.address);
      } else {
        // Fallback: Search for business name with full address
        appleMapsUrl = `maps://maps.apple.com/?q=${encodeURIComponent(location.name + ' ' + location.address)}`;
        console.log('Apple Maps URL (address search fallback):', appleMapsUrl);
      }

      // Try to open Apple Maps to the specific location
      window.location.href = appleMapsUrl;
      
      // Fallback to Google Maps if Apple Maps doesn't open
      setTimeout(() => {
        console.log('Falling back to Google Maps:', googleMapsUrl);
        window.open(googleMapsUrl, '_blank');
      }, 2500);
      
    } else {
      // For non-iOS devices, open Google Maps directly
      window.open(googleMapsUrl, '_blank');
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      minHeight: '100vh',
      paddingTop: hasResults ? '4vh' : '20vh',
      paddingBottom: '4vh',
      paddingLeft: '16px',
      paddingRight: '16px',
      fontFamily: 'Arial, sans-serif',
      transition: 'padding-top 0.3s ease',
    },
    heroSection: {
      textAlign: 'center',
      marginBottom: '40px',
    },
    mainTitle: {
      fontSize: '36px',
      marginBottom: '10px',
    },
    tagline: {
      fontSize: '18px',
      color: '#666',
    },
    subTagline: {
      fontSize: '14px',
      color: '#666',
    },
    title: {
      marginBottom: '24px',
      fontSize: '24px',
      textAlign: 'center',
      padding: '0 16px',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      width: '100%',
      maxWidth: '400px',
      marginBottom: '24px',
    },
    inputContainer: {
      display: 'flex',
      width: '100%',
      maxWidth: '400px',
      padding: '0 8px',
    },
    inputWrapper: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '5px 10px',
      marginRight: '10px',
    },
    input: {
      flex: 1,
      border: 'none',
      outline: 'none',
      fontSize: '16px',
    },
    button: {
      padding: '10px 20px',
      fontSize: '16px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '60px',
      transition: 'background-color 0.2s',
      '&:disabled': {
        backgroundColor: '#007bff', // Keep the same color while loading
        opacity: 0.7,
        cursor: 'not-allowed',
      }
    },
    suggestions: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1,
      listStyle: 'none',
      margin: 0,
      padding: 0,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderTop: 'none',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      maxHeight: '200px',
      overflowY: 'auto',
    },
    suggestionItem: {
      padding: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
    },
    logo: {
      width: '20px',
      height: '20px',
      marginRight: '10px',
      objectFit: 'contain',
    },
    selectedLogo: {
      width: '20px',
      height: '20px',
      marginRight: '10px',
      objectFit: 'contain',
    },
    focusedItem: {
      backgroundColor: '#f0f0f0',
    },
    aiResultsContainer: {
      marginTop: '16px',
      padding: '20px',
      backgroundColor: '#f0f0f0',
      borderRadius: '8px',
      maxWidth: '600px',
      width: '100%',
      marginBottom: '16px',
    },
    aiResultsTitle: {
      fontSize: '18px',
      marginBottom: '10px',
    },
    aiResultsContent: {
      backgroundColor: 'white',
      padding: '15px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    locationsContainer: {
      marginTop: '10px',
    },
    locationsList: {
      listStyle: 'none',
      padding: '0 0 0 20px',
      margin: '5px 0',
    },
    locationItem: {
      padding: '4px 0',
      fontSize: '14px',
      color: '#666',
    },
    aiResultsText: {
      fontSize: '14px',
      lineHeight: '1.5',
      marginBottom: '8px',
    },
    uploadContainer: {
      marginTop: '20px',
      padding: '15px',
      backgroundColor: '#f0f0f0',
      borderRadius: '4px',
      maxWidth: '600px',
      width: '100%',
    },
    uploadButton: {
      marginLeft: '10px',
      padding: '5px 10px',
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    rank: {
      fontSize: '12px',
      color: '#666',
      marginLeft: '10px',
    },
    nearestLocation: {
      marginTop: '10px',
      padding: '10px',
      backgroundColor: '#e8f5e9',
      borderRadius: '4px',
    },
    distance: {
      fontSize: '14px',
      color: '#666',
      margin: '4px 0',
      textAlign: 'center',
      width: '100%',
    },
    spinner: {
      animation: 'spin 1s linear infinite',
      fontSize: '18px',
      display: 'inline-block',
    },
    locationsSection: {
      marginTop: '16px',
      width: '100%',
      maxWidth: '600px',
      padding: '0',
      margin: '16px auto 0',
    },
    locationTitle: {
      fontSize: '20px',
      marginBottom: '16px',
      color: '#333',
      textAlign: 'center',
    },
    boxContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      width: '100%',
      justifyContent: 'center',
      alignItems: 'stretch',
    },
    locationBox: {
      flex: '1',
      width: '100%',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid #ddd',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    boxNumber: {
      fontSize: '16px',
      fontWeight: 'normal',
      color: '#666',
      textAlign: 'center',
      width: '100%',
      marginBottom: '8px',
    },
    locationContent: {
      padding: '16px',
      textAlign: 'center',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
    },
    locationName: {
      fontSize: '16px',
      fontWeight: 'bold',
      margin: '8px 0',
      textAlign: 'center',
      width: '100%',
    },
    locationAddress: {
      margin: '8px 0',
      color: '#2196F3',
      fontSize: '14px'
    },
    openStatus: {
      fontSize: '14px',
      color: '#4CAF50',
      margin: '4px 0',
      textAlign: 'center',
      width: '100%',
    },
    rating: {
      fontSize: '12px',
      color: '#FFA000',
    },
    locationPhone: {
      fontSize: '14px',
      color: '#007bff',
      marginBottom: '8px',
    },
    phoneLink: {
      textDecoration: 'none',
      color: '#007bff',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '@media (min-width: 768px)': {
      boxContainer: {
        // Remove this since we're using the same layout for all screens
      },
      locationBox: {
        // Remove this since we're using the same layout for all screens
      },
    },
    websiteLink: {
      color: '#007bff',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      }
    },
    '@keyframes pulse': {
      '0%': {
        opacity: 0.6,
      },
      '50%': {
        opacity: 1,
      },
      '100%': {
        opacity: 0.6,
      }
    },
    addressLink: {
      color: '#2196F3',
      textDecoration: 'none',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      width: '100%',
      '&:hover': {
        textDecoration: 'underline'
      }
    },

    // Logo styles
    brandLogo: {
      width: '80px',
      height: '80px',
      objectFit: 'contain',
      borderRadius: '8px',
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      flexShrink: 0,
      marginRight: '20px', // Add spacing between logo and content
      padding: '8px', // Add internal padding to the logo
    },
    brandInfo: {
      flex: 1,
      paddingLeft: '10px', // Add extra padding to the text content
    },
    attribution: {
      textAlign: 'center',
      marginTop: '10px',
      fontSize: '12px',
      color: '#666',
    },
    attributionLink: {
      color: '#666',
      textDecoration: 'none',
    },

  };

  return (
    <div style={styles.container}>
      <div style={styles.heroSection}>
        <h1 style={styles.mainTitle}>SpeedFind</h1>
        <p style={styles.tagline}>The fastest way to find known brands</p>
        <p style={styles.subTagline}>One search. One result. One tap away.</p>
      </div>
      <form onSubmit={handleSearch} style={styles.form}>
        <div style={styles.inputContainer}>
          <div style={styles.inputWrapper}>
            {selectedBrand && selectedBrand.logo && (
              <img 
                src={selectedBrand.logo} 
                alt={selectedBrand.name} 
                style={styles.selectedLogo} 
              />
            )}
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Search for a brand..."
              style={styles.input}
            />
          </div>
          <button 
            type="submit" 
            style={styles.button}
            disabled={isLoading}
          >
            {isLoading ? (
              <FontAwesomeIcon 
                icon={faSpinner} 
                style={styles.spinner}
              />
            ) : (
              'Go!'
            )}
          </button>
        </div>
      </form>
      

      
      {isLoading ? (
        <SkeletonLoader />
      ) : (
        aiResults && (
          <>
            <div style={styles.aiResultsContainer}>
              <div style={styles.aiResultsContent}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  {aiResults.logo && (
                    <img 
                      src={aiResults.logo} 
                      alt={aiResults.brandName} 
                      style={styles.brandLogo} 
                    />
                  )}
                  <div style={styles.brandInfo}>
                    <p><strong>Brand:</strong> {aiResults.brandName}</p>
                    {aiResults.website && (
                      <p>
                        <strong>Website:</strong>{' '}
                        <a 
                          href={aiResults.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={styles.websiteLink}
                        >
                          {aiResults.website}
                        </a>
                      </p>
                    )}
                    {aiResults.phone && (
                      <p>
                        <strong>Phone:</strong>{' '}
                        <a href={`tel:${aiResults.phone}`}>
                          {aiResults.phone}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
            
              </div>
            </div>

            {/* Locations Section */}
            <div style={styles.locationsSection}>
              {locationResults && locationResults.length > 0 && (
                <>
                  <h2 style={styles.locationTitle}>Nearby Locations</h2>
                  <div style={styles.boxContainer} className="box-container">
                    {locationResults.map((location, index) => (
                      <div key={index} style={styles.locationBox} className="location-box">
                        <div style={styles.locationContent}>
                          <div style={styles.boxNumber}>
                            {index === 0 ? '🎯 Closest Location' : `Location ${index + 1}`}
                          </div>
                          <p style={styles.locationName}>{location.name}</p>
                          <button 
                            style={styles.addressLink}
                            onClick={() => openInMaps(location)}
                          >
                            <p style={styles.locationAddress}>{location.address}</p>
                          </button>
                          {location.distance && (
                            <p style={styles.distance}>
                              {formatDistance(location.distance)}
                            </p>
                          )}
                          {location.phone && (
                            <p style={styles.locationPhone}>
                              <a href={`tel:${location.phone}`} style={styles.phoneLink}>
                                {formatPhoneNumber(location.phone)}
                              </a>
                            </p>
                          )}
                          {location.open_now !== undefined && (
                            <p style={styles.openStatus}>
                              {location.open_now ? '✅ Open Now' : '❌ Closed'}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )
      )}
    </div>
  );
}

export default HomePage;
